import { useMemo, FC, useState } from "react";
import styled from "@emotion/styled";
import {
  MDSActionMenuWithContent,
  MDSButton,
  MDSIcon,
  MDSIconKind,
  MDSMenuContentItem,
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuDropdownPopover,
  MDSMenuDropdownSlotName,
  MDSMenuItemSelectionVariant,
} from "@/mds";
import { DialogTrigger } from "react-aria-components";
import { SettingsTabBarSectionContent } from "./settings-tab-bar.types";
import { SettingsTabBarSection } from "./settings-tab-bar-section";

export interface SettingsTabBarProps {
  selectedItemId: string;
  sections: SettingsTabBarSectionContent[];
}

export const SettingsTabBar: FC<SettingsTabBarProps> = ({ selectedItemId, sections }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownItems: MDSMenuContentList = useMemo(() => {
    const items: MDSMenuContentItem[] = sections.flatMap(section => {
      const sectionItems = section.items.map(item => ({
        id: item.id,
        kind: MDSMenuContentItemKind.Item,
        label: item.label,
        icon: item.icon,
        isSelected: item.id === selectedItemId,
        selectionVariant: MDSMenuItemSelectionVariant.Checkmark,
        onClick: () => item.onClick({ itemId: item.id }),
      }));
      const sectionHeader: MDSMenuContentItem[] = [
        {
          id: `section-header-${section.id}`,
          kind: MDSMenuContentItemKind.Header,
          label: section.label,
        },
      ];
      return [...sectionHeader, ...sectionItems];
    });
    return items;
  }, [sections, selectedItemId]);

  // Find the active item label for the dropdown button
  const activeItemLabel = useMemo(() => {
    for (const section of sections) {
      const activeItem = section.items.find(item => item.id === selectedItemId);
      if (activeItem) {
        return activeItem.label;
      }
    }
    return "";
  }, [sections, selectedItemId]);

  return (
    <TabBarContainer>
      <Container>
        <DesktopContent>
          {sections.map((section, index) => (
            <SettingsTabBarSection
              isFirst={index === 0}
              key={section.id}
              selectedItemId={selectedItemId}
              section={section}
            />
          ))}
        </DesktopContent>
        <DialogTrigger onOpenChange={setIsDropdownOpen}>
          <MobileDropdownButton>
            <MobileDropdownButtonContent>
              {activeItemLabel}
              <MDSIcon kind={isDropdownOpen ? MDSIconKind.AngleUp : MDSIconKind.AngleDown} />
            </MobileDropdownButtonContent>
          </MobileDropdownButton>
          <StyledMenuDropdownPopover offset={-42} placement="bottom">
            <StyledMenu contentList={dropdownItems} slot={MDSMenuDropdownSlotName.Menu} />
          </StyledMenuDropdownPopover>
        </DialogTrigger>
      </Container>
    </TabBarContainer>
  );
};

const Container = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  maxWidth: 300,
  height: "100%",
  padding: `0 ${theme.spacing[3]} ${theme.spacing[2]}`,

  "@container mds-panel (max-width: 750px)": {
    padding: 0,
    height: "auto",
    margin: `0 auto`,
  },
}));

const TabBarContainer = styled.div(({ theme }) => ({
  flex: "1 0 280px",
  "@container mds-panel (max-width: 750px)": {
    flex: "unset",
    width: "100%",
    paddingBottom: theme.spacing[2],
    gap: theme.spacing[2],
  },
}));

const DesktopContent = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  "@container mds-panel (max-width: 750px)": {
    display: "none",
  },
});

const StyledMenuDropdownPopover = styled(MDSMenuDropdownPopover)({
  width: "calc(100vw - 32px)",
  maxWidth: "370px",
});

const StyledMenu = styled(MDSActionMenuWithContent)({
  maxWidth: "unset",
});

const MobileDropdownButtonContent = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: theme.spacing[1],
}));

const MobileDropdownButton = styled(MDSButton)({
  display: "none",
  width: "100%",
  "> button": {
    width: "100%",
  },
  "@container mds-panel (max-width: 750px)": {
    display: "block",
  },
});
